import { useEffect, useState } from 'react';
import { Badge } from './ui/badge';
import { Server, AlertCircle, Eye, X } from 'lucide-react';
import { Button } from './ui/button';

export default function EnvironmentIndicator() {
  const [apiUrl, setApiUrl] = useState<string>('');
  const [isLocal, setIsLocal] = useState<boolean>(false);
  const [isDevelopment, setIsDevelopment] = useState<boolean>(false);
  const [isHidden, setIsHidden] = useState<boolean>(false);

  useEffect(() => {
    const url = process.env['REACT_APP_API_URL'] || 'http://localhost:8000/api';
    setApiUrl(url);
    setIsLocal(url.includes('localhost') || url.includes('127.0.0.1'));
    setIsDevelopment(process.env['NODE_ENV'] === 'development');

    // Load hidden state from localStorage
    const hidden = localStorage.getItem('envIndicatorHidden') === 'true';
    setIsHidden(hidden);
  }, []);

  const handleHide = () => {
    setIsHidden(true);
    localStorage.setItem('envIndicatorHidden', 'true');
  };

  const handleShow = () => {
    setIsHidden(false);
    localStorage.setItem('envIndicatorHidden', 'false');
  };

  // Only show in development mode
  if (!isDevelopment) {
    return null;
  }

  // Show minimized button when hidden
  if (isHidden) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          size="sm"
          variant="outline"
          onClick={handleShow}
          className="shadow-lg"
          title="Show environment info"
        >
          <Eye className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-card border border-border rounded-lg shadow-lg p-3 max-w-xs">
        <div className="flex items-center gap-2 mb-2">
          <Server className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Environment</span>
        </div>
        
        <div className="space-y-2">
          {/* Environment Badge */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Mode:</span>
            <Badge 
              variant={isLocal ? "default" : "destructive"}
              className="text-xs"
            >
              {isLocal ? (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Local
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Production
                </span>
              )}
            </Badge>
          </div>

          {/* API URL */}
          <div className="text-xs">
            <span className="text-muted-foreground">API:</span>
            <div className="font-mono text-[10px] bg-muted p-1 rounded mt-1 break-all">
              {apiUrl}
            </div>
          </div>

          {/* Node Environment */}
          <div className="text-xs text-muted-foreground">
            Node: {process.env.NODE_ENV}
          </div>
        </div>

        {/* Hide button */}
        <div className="mt-3 pt-2 border-t border-border">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleHide}
            className="w-full text-xs h-7"
          >
            <X className="h-3 w-3 mr-1" />
            Hide
          </Button>
        </div>
      </div>
    </div>
  );
}

